import { getRoleId } from "./lastPathUtil";
import { Location, NavigateFunction } from "react-router-dom";
import { AlertServerPayload } from "redux/stomp/stompMapSlice";

export const AlertTypeFilter = [
  {
    label: "Health",
    value: "HEALTH",
  },
  {
    label: "Inventory",
    value: "INVENTORY",
  },
];

export const AlertRecorerTypeFilter = [
  {
    label: "SPECTRUM",
    value: "SPECTRUM",
  },
  {
    label: "VMAX-IP",
    value: "VMAX-IP",
  },
  {
    label: "VMAX-A1",
    value: "VMAX-A1",
  },
  {
    label: "COVA",
    value: "COVA",
  },
];

export const AlertStatusTypeFilter = [
  {
    label: "Active",
    value: "ACTIVE",
  },
  {
    label: "Assign",
    value: "ASSIGNED",
  },
  {
    label: "Closed",
    value: "CLOSED",
  },
];

export const ActiveAlertStatusTypeFilter = [
  {
    label: "Active",
    value: "ACTIVE",
  },
  {
    label: "Assign",
    value: "ASSIGNED",
  },
];

export const AlertProbableCause = {
  "Video Loss": [
    "- The camera cable is unplugged or defective.",
    "- Camera hardware issue.",
    "- PoE switch is offline or defective.",
    "- Camera is powered off.",
  ],
  "Recording Failed": [
    "- Failed to access all HDDs",
    "- Hardware issue",
    "- Recording software issue",
  ],
  "System Not Responding": [
    "- Power interruption",
    "- Network issue",
    "- Hardware issue",
  ],
  "Abnormal Restart": [
    "- Power interruption",
    "- Software issue(Watchdog)",
    "- Hardware issue",
  ],
  "CPU Overheated": ["- Hardware issue", "- Environmental issue"],
  "Hardware failure": ["- HDD physical damage", "- Hardware issue"],
  "Disk Overheated": ["- HDD physical damage", "- Hardware issue"],
  "BAD Sector": ["- HDD physical damage", "- Hardware issue"],
  "Storage status being checked": [
    "- HDD physical damage",
    "- Hardware issue",
    "- Invalid setup",
    "- One or more storage devices are being checked by media server",
  ],
  Reindexing: [
    "- HDD physical damage",
    "- Hardware issue",
    "- Invalid setup",
    "- Reindexing of one or more storage devices are taking more than 24 Hours.",
  ],
  "Storage space near full": [
    "- HDD physical damage",
    "- Hardware issue",
    "- Invalid setup",
    "- One or more storage devices are almost full",
  ],
  "Reserved space not clearing for over": [
    "- HDD physical damage",
    "- Hardware issue",
    "- Invalid setup",
    "- Recording retention is not working.",
  ],
};

export const changeAlertReasonToCategory = (alertReason: string) => {
  if (alertReason === "Video Loss") return "Video";
  else if (
    alertReason === "Abnormal Restart" ||
    alertReason === "System Not Responding" ||
    alertReason === "CPU Overheated" ||
    alertReason === "Recording Failed"
  )
    return "System";
  else return "Disk";
};

export const filteringDiskAlertBoolean = ({
  category,
  type,
}: {
  category?: string;
  type: string;
}) => {
  if (category !== undefined)
    return (
      changeAlertReasonToCategory(type) === "Disk" &&
      type !== "Hardware Failure"
    );
  else return category === "Disk" && type !== "Hardware Failure";
};

export const cameraUniqueId = ({
  cameraId,
  channel,
}: {
  cameraId: string;
  channel: number;
}) => {
  return `${cameraId}:${channel}`;
};

export const storageUniqueId = ({
  cameraId,
  channel,
}: {
  cameraId: string;
  channel: number;
}) => {
  return `${cameraId}:${channel}`;
};

export const displayAlert = (
  row: any
): {
  type: string;
  detail: string;
} => {
  let display: string = "";
  const alertTypeStr =
    row.alertTypeString !== undefined ? row.alertTypeString : row.alertType;
  if (!alertTypeStr) {
    return {
      type: "",
      detail: "",
    };
  }

  switch (alertTypeStr) {
    case "Video Loss":
      display =
        row.cameraName !== undefined && row.cameraName === ""
          ? ` [${row.channel}]`
          : ` [${row.cameraName}]`;
      break;
    case "Storage space near full":
      display =
        row.storageName !== undefined && row.storageName !== ""
          ? ` [${row.storageName}]`
          : ``;

      break;
    default:
      display = "";
      break;
  }

  return {
    type: alertTypeStr,
    detail: display,
  };
};

export const AlertTypeEnumKeys = {
  //SYSTEM
  SYSTEM_NOT_RESPONDING: "System Not Responding",
  ABNORMAL_RESTART: "Abnormal Restart",
  CPU_OVERHEATED: "CPU Overheated",
  //DISK
  HARDWARE_FAILURE: "Hardware failure",
  DISK_OVERHEATED: "Disk Overheated",
  BAD_SECTOR: "BAD Sector",
  STORAGE_STATUS_BEING_CHECKED: "Storage status being checked",
  REINDEXING: "Reindexing",
  STORAGE_SPACE_NEAR_FULL: "Storage space near full",
  RESERVED_SPACE_NOT_CLEAR: "Reserved space not clearing for over",
  //VIDEO
  VIDEO_LOSS: "Video Loss",
  RECORDING: "Recording Failed",
} as const;

export type AlertTypeEnum =
  typeof AlertTypeEnumKeys[keyof typeof AlertTypeEnumKeys];

const segment = (v: string | number | null | undefined) =>
  String(v ?? "na").replace(/:/g, "%3A");

const is30to36 = (n: number) => n >= 30 && n <= 36;

/**
 * 규칙:
 * - 기본: {recorderId}:{alertTypeInt}
 * - alertTypeInt === 40: {recorderId}:{alertTypeInt}:{cameraId}:{channel}
 * - alertTypeInt ∈ [30..36]: {recorderId}:{alertTypeInt}:{storageName}
 */
export function buildAlertEntityId(alertEnt: AlertServerPayload): string {
  if (alertEnt.alertTypeInt === 40) {
    return [
      segment(alertEnt.recorderId),
      alertEnt.alertTypeInt,
      segment(alertEnt.cameraId),
      segment(alertEnt.channel),
    ].join(":");
  }

  if (is30to36(alertEnt.alertTypeInt)) {
    return [
      segment(alertEnt.recorderId),
      alertEnt.alertTypeInt,
      segment(alertEnt.storageName),
    ].join(":");
  }

  return [segment(alertEnt.recorderId), alertEnt.alertTypeInt].join(":");
}

export const navigateAlertDetailPage = ({
  location,
  navigate,
  accountId,
  recorderId,
  historyId,
  alertType,
  cameraId,
  channel,
}: {
  location: Location;
  navigate: NavigateFunction;
  accountId: string;
  recorderId: string;
  historyId: string;
  alertType: string;
  cameraId?: string;
  channel?: number;
}) => {
  const params = new URLSearchParams({
    recorderId,
    historyId,
    alertType,
    cameraId: cameraId ?? "",
    channel: channel?.toString() ?? "",
    accountId,
    r: getRoleId({
      accountId: accountId,
    }) as string,
  }).toString();

  const basePathName = location.pathname.includes("/alert/history")
    ? location.pathname
    : "/alert/active";

  const from = `${location.pathname}${location.search}`;
  const pathName = `${basePathName}/view`;

  navigate(`${pathName}?${params}`, {
    state: { from },
  });
};

export const enum ALERT_CATEGORY_TYPE {
  SYSTEM = "System",
  DISK = "Disk",
  VIDEO = "Video",
}

export const AlertCategoryTypeFilter = [
  {
    label: "System",
    value: ALERT_CATEGORY_TYPE.SYSTEM,
  },
  {
    label: "Disk",
    value: ALERT_CATEGORY_TYPE.DISK,
  },
  {
    label: "Video",
    value: ALERT_CATEGORY_TYPE.VIDEO,
  },
];
